import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resetState } from '../redux/imagesSlice';
import { WhatsappButton } from './buttons/WhatsappButtonComponent';

function ScoreRing({ score }) {
    const radius = 70;
    const stroke = 8;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;

    let ringColor = 'stroke-red-400';
    if (score >= 70) ringColor = 'stroke-emerald-400';
    else if (score >= 40) ringColor = 'stroke-amber-400';

    const center = radius + stroke;
    const size = 2 * center;

    return (
        <div className="inline-flex items-center justify-center">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                {/* Background ring */}
                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    strokeWidth={stroke}
                    className="stroke-gray-200"
                />
                {/* Score ring */}
                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    strokeWidth={stroke}
                    strokeLinecap="round"
                    className={`${ringColor} transition-all duration-1000 ease-out`}
                    style={{
                        strokeDasharray: circumference,
                        strokeDashoffset: offset,
                        transformOrigin: 'center',
                        transform: 'rotate(-90deg)',
                    }}
                />
                {/* Score text */}
                <text
                    x={center}
                    y={center}
                    dy="-6"
                    textAnchor="middle"
                    dominantBaseline="central"
                    className="font-title fill-[#0F172A]"
                    style={{ fontSize: '3rem' }}
                >
                    {score}%
                </text>
            </svg>
        </div>
    );
}

function getScoreMessage(score) {
    if (score === 100)
        return { text: 'Perfect score!', icon: 'fa-solid fa-trophy', iconColor: 'text-amber-400' };
    if (score >= 70)
        return { text: 'Great job!', icon: 'fa-solid fa-face-laugh-beam', iconColor: 'text-emerald-400' };
    if (score >= 40)
        return { text: 'Not bad!', icon: 'fa-solid fa-face-smile', iconColor: 'text-amber-400' };
    return { text: 'Better luck next time!', icon: 'fa-solid fa-face-meh', iconColor: 'text-blue-400' };
}

function Recap() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [copied, setCopied] = useState(false);

    const resultsArray = useSelector((state) => state.imageClicked.resultsArray);
    const quizId = useSelector((state) => state.imageClicked.quizId);
    const imagesArray = useSelector((state) => state.imageClicked.imagesArray);
    const rightAnswers = useSelector((state) => state.imageClicked.rightAnswers);
    const userId = useSelector((state) => state.imageClicked.userId);

    const score =
        imagesArray.length > 0
            ? Math.round((rightAnswers / imagesArray.length) * 100)
            : 0;

    const hasPosted = useRef(false);

    useEffect(() => {
        if (imagesArray.length === 0 && quizId === 0) {
            navigate('/', { replace: true });
            return;
        }

        if (hasPosted.current) return;
        hasPosted.current = true;

        fetch(process.env.REACT_APP_DATABASE_URL + '/stats', {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                resultsArray: resultsArray,
                quizId: quizId,
                score: score,
                userId: userId,
            }),
        });
    }, [imagesArray.length, quizId, navigate, resultsArray, score, userId]);

    const resetSt = () => {
        dispatch(resetState());
        navigate('/game');
    };

    const shareUrl =
        (process.env.REACT_APP_FRONTEND_URL || window.location.origin) +
        '/game/' +
        quizId;

    const shareText = `Hey, I just scored ${score}% on Kid or Dog! Think you can beat me? Try it here: ${shareUrl}`;

    const canNativeShare = typeof navigator.share === 'function';

    const nativeShare = async () => {
        try {
            await navigator.share({
                text: shareText,
            });
        } catch (err) {
            // User cancelled or share failed — ignore
        }
    };

    const copyLink = () => {
        navigator.clipboard.writeText(shareUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const { text: message, icon, iconColor } = getScoreMessage(score);

    return (
        <div className="h-[100dvh] flex flex-col bg-gradient-to-b from-[#F5F3FF] to-[#F0FDFA] overflow-hidden">
            {/* Title */}
            <div className="pt-8 text-center">
                <h1
                    className="font-title text-3xl text-[#0F172A] cursor-pointer"
                    onClick={() => navigate('/')}
                >
                    Kid or Dog?
                </h1>
            </div>

            {/* Score area */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 gap-4">
                <ScoreRing score={score} />

                <div className="text-center">
                    <i className={`${icon} ${iconColor} text-3xl mb-2 block`}></i>
                    <h2 className="font-title text-2xl text-[#0F172A]">
                        {message}
                    </h2>
                    <p className="text-[#334155] text-sm mt-1">
                        {rightAnswers} out of {imagesArray.length} correct
                    </p>
                </div>

                {/* Share row */}
                <div className="flex items-center justify-center gap-3 mt-2">
                    {canNativeShare ? (
                        <button
                            onClick={nativeShare}
                            className="
                                h-10 px-5 rounded-full
                                bg-[#0F172A] hover:bg-[#111827]
                                text-white font-semibold text-sm
                                flex items-center justify-center gap-2
                                active:scale-95 transition-all duration-200
                            "
                            title="Share"
                        >
                            <i className="fa-solid fa-share-nodes"></i>
                            Share
                        </button>
                    ) : (
                        <>
                            <WhatsappButton quizId={quizId} score={score} />

                            <a
                                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                                rel="noopener noreferrer"
                                className="
                                    w-10 h-10 rounded-full
                                    bg-[#1877F2] hover:bg-[#166FE5]
                                    flex items-center justify-center
                                    transition-colors duration-200
                                    no-underline
                                "
                                title="Share on Facebook"
                            >
                                <i className="fa-brands fa-facebook-f text-white text-sm"></i>
                            </a>

                            <button
                                onClick={copyLink}
                                className="
                                    w-10 h-10 rounded-full
                                    bg-gray-200 hover:bg-gray-300
                                    flex items-center justify-center
                                    transition-colors duration-200
                                "
                                title="Copy link"
                            >
                                <i
                                    className={`fa-solid ${
                                        copied ? 'fa-check text-emerald-500' : 'fa-link text-gray-500'
                                    } text-sm`}
                                ></i>
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Play Again */}
            <div className="px-6 pb-8 pt-4">
                <button
                    onClick={resetSt}
                    className="
                        w-full h-14 rounded-xl
                        bg-[#0F172A] hover:bg-[#111827]
                        text-white font-semibold text-lg
                        shadow-[0_4px_20px_rgba(15,23,42,0.3)]
                        hover:shadow-[0_6px_25px_rgba(15,23,42,0.4)]
                        active:scale-95
                        transition-all duration-200
                        flex items-center justify-center gap-2
                    "
                >
                    <i className="fa-solid fa-rotate-right"></i>
                    Play Again
                </button>
            </div>
        </div>
    );
}

export default Recap;
